// src/components/Admin/SalasCmp/SalasItem_Salas.jsx
import React, { useState } from "react";
import { Icon } from "semantic-ui-react";
import { Button, Tooltip } from "antd";
import {
  EyeOutlined,
  UnorderedListOutlined,
  LoginOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MozillaHubRoomsApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { NEW_SALAS } from "../../../../ui/featureFlags";
import { getCategoryColor } from "../categoryMeta";
import sx from "./SalasItem_Salas.module.scss";
import "./SalasItem_Salas.scss";

const CIRCLE_COLOR_MAP = {
  "CIENCIAS HUMANAS": "circle--ciencias-humanas",
  "CIENCIAS NATURALES": "circle--ciencias-naturales",
  HISTORIA: "circle--historia",
  MATEMATICAS: "circle--matematicas",
  ARTE: "circle--arte",
  "VIDA SALUDABLE": "circle--vida-saludable",
  OTROS: "circle--gray",
};

const mozillaHubRoomsApi = new MozillaHubRoomsApi();

export function SalasItemSalas({ scene, onUploadScene }) {
  const navigate = useNavigate();
  const { t } = useTranslation("scenes");
  const { accessToken } = useAuth();
  const [loadingScene, setLoadingScene] = useState(false);
  const [errorScene, setErrorScene] = useState(null);

  const normalizeCategory = (cat) =>
    cat
      .trim()
      .toUpperCase()
      .replace(/Á/g, "A")
      .replace(/É/g, "E")
      .replace(/Í/g, "I")
      .replace(/Ó/g, "O")
      .replace(/Ú/g, "U");

  const getCircleColorClass = (cat) =>
    CIRCLE_COLOR_MAP[normalizeCategory(cat)] || "circle--gray";

  const handleInfoClick = () => {
    navigate(`/teacher/guia/${scene.refId}`);
  };

  const handleUploadClick = () => {
    onUploadScene?.(scene);
  };

  const handleEnterClick = async () => {
    try {
      setLoadingScene(true);
      setErrorScene(null);

      const url = scene.urlsala;
      // 1) Comprobamos si es una escena de Mozilla Hub
      const isHub = url.startsWith("https://educa360.es/scenes/");

      if (isHub) {
        // 2) Extraemos el shortLink
        const cleaned = url.replace(/\/+$/, "");
        const shortLink = cleaned.split("/").pop();

        // 3) Llamada a LoadRoom. `source` clasifica la apertura en el dashboard
        //    admin: desde el catalogo de Salas es un OJEO del profesor.
        const result = await mozillaHubRoomsApi.LoadRoom(
          accessToken,
          shortLink,
          { source: "teacher_salas", sceneRefId: scene.refId },
        );

        // 4) Montamos la URL embed y la abrimos
        const { id, embedToken } = result.room;
        const finalUrl = `https://educa360.es/${id}?embed_token=${embedToken}`;
        window.open(finalUrl, "_blank", "noopener,noreferrer");
      } else {
        // No es Hub: abrimos directamente la URL que trae scene.urlsala.
        // Como no pasa por /roomshub/load, la apertura se anota con la baliza.
        mozillaHubRoomsApi.trackRoomOpen(accessToken, {
          sceneRefId: scene.refId,
          url,
          title: scene.title,
          source: "teacher_salas",
        });
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (err) {
      console.error("Error cargando la sala:", err);
      setErrorScene(err.message || t("item.unknownError"));
    } finally {
      setLoadingScene(false);
    }
  };

  if (NEW_SALAS) {
    const cats = Array.isArray(scene.category)
      ? scene.category.filter(Boolean)
      : [];
    const subs = Array.isArray(scene.subcategory)
      ? scene.subcategory.filter(Boolean)
      : [];

    return (
      <div className={sx.card}>
        <div className={sx.thumb}>
          {loadingScene && (
            <div className={sx.loadingOverlay}>{t("item.loadingSala")}</div>
          )}
          <img src={scene.bannertopUrl} alt={scene.title} loading="lazy" />
        </div>

        <div className={sx.body}>
          <p className={sx.createdBy}>{t("item.createdBy")}</p>

          {cats.length ? (
            <div className={sx.cats}>
              {cats.map((cat) => (
                <span className={sx.cat} key={cat}>
                  <span
                    className={sx.dot}
                    style={{ background: getCategoryColor(cat) }}
                  />
                  {cat}
                </span>
              ))}
            </div>
          ) : null}

          {subs.length ? <p className={sx.sub}>{subs.join(", ")}</p> : null}

          <h3 className={sx.title} title={scene.title}>
            {scene.title}
          </h3>

          {errorScene && <p className={sx.errorText}>{errorScene}</p>}

          <div className={sx.divider} />

          <div className={sx.actions}>
            <Tooltip title={t("item.enterScene")}>
              <Button
                className={sx.actBtn}
                icon={<EyeOutlined />}
                loading={loadingScene}
                onClick={handleEnterClick}
                aria-label={t("item.enterScene")}
              />
            </Tooltip>
            <Tooltip title={t("item.viewGuide")}>
              <Button
                className={sx.actBtn}
                icon={<UnorderedListOutlined />}
                onClick={handleInfoClick}
                aria-label={t("item.viewGuide")}
              />
            </Tooltip>
            <Tooltip title={t("item.addToItinerary")}>
              <Button
                className={sx.actMain}
                type="primary"
                icon={<LoginOutlined />}
                onClick={handleUploadClick}
              >
                {t("item.addToItinerary")}
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ui-materials">
      <div className="e-card-scene">
        {loadingScene && (
          <div className="loading-overlay">{t("item.loadingSala")}</div>
        )}
        <img
          className="card-banner"
          src={scene.bannertopUrl}
          alt={scene.title}
        />
        <div className="card-body">
          {errorScene && <p className="error-text">¡{errorScene}!</p>}
          <p className="card-createdby">{t("item.createdBy")}</p>
          <div className="card-circles">
            {scene.category?.map((cat) => (
              <div key={cat} className={`circle ${getCircleColorClass(cat)}`} />
            ))}
            {scene.subcategory && (
              <h4 className="card-subcategorie">
                {scene.subcategory.join(", ")}
              </h4>
            )}
          </div>
          <h3 className="card-title">{scene.title}</h3>
          <hr className="card-divider" />
          <div className="card-actions">
            <div className="tooltip-container">
              <button
                className="btn-icon"
                onClick={handleEnterClick}
                disabled={loadingScene}
              >
                <Icon name="eye" />
              </button>
              <span className="tooltip-text">{t("item.enterScene")}</span>
            </div>
            <div className="tooltip-container">
              <button className="btn-icon" onClick={handleInfoClick}>
                <Icon name="list" />
              </button>
              <span className="tooltip-text">{t("item.viewGuide")}</span>
            </div>
            <div className="tooltip-container">
              <button className="btn-add-route" onClick={handleUploadClick}>
                <Icon className="btn-add-route__ico" name="sign-in" />
              </button>
              <span className="tooltip-text">{t("item.addToItinerary")}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
